import React, { useState, useEffect } from 'react';
import { createPortal } from 'react-dom';
import { X, Plus, Pencil, Trash2, ChevronUp, ChevronDown } from 'lucide-react';
import DatasetEditorModal from './DatasetEditorModal';
import './WidgetModal.css';

const CUSTOM_TABLE_LOG_FIELDS = [
  { value: 'timestamp', label: 'Timestamp' },
  { value: 'level', label: 'Level' },
  { value: 'service', label: 'Service' },
  { value: 'message', label: 'Message' },
  { value: 'traceId', label: 'Trace ID' },
  { value: 'userId', label: 'User ID' },
  { value: 'ip', label: 'IP address' },
  { value: 'userAgent', label: 'User agent' },
  { value: 'id', label: 'Log ID' }
];

const CUSTOM_TABLE_PRESET_FIELD_SET = new Set(CUSTOM_TABLE_LOG_FIELDS.map((f) => f.value));

const CUSTOM_TABLE_VALUE_TYPES = [
  { value: 'auto', label: 'Auto (detect from value)' },
  { value: 'text', label: 'Text' },
  { value: 'keyword', label: 'Keyword (compact, one line)' },
  { value: 'integer', label: 'Whole number' },
  { value: 'number', label: 'Decimal number' },
  { value: 'datetime', label: 'Date & time' },
  { value: 'date', label: 'Date only' }
];

const CUSTOM_FIELD_SELECT_CUSTOM = '__custom__';

function migrateCustomTableColumn(col) {
  if (!col || typeof col !== 'object') return col;
  const field = typeof col.field === 'string' ? col.field : 'message';
  const fieldMode =
    col.fieldMode === 'custom' || col.fieldMode === 'preset'
      ? col.fieldMode
      : CUSTOM_TABLE_PRESET_FIELD_SET.has(field)
        ? 'preset'
        : 'custom';
  const allowedVt = new Set(CUSTOM_TABLE_VALUE_TYPES.map((t) => t.value));
  const valueType = allowedVt.has(col.valueType) ? col.valueType : 'auto';
  return { ...col, field, fieldMode, valueType };
}

const newColumnId = () => `col-${Date.now()}-${Math.random().toString(36).slice(2, 9)}`;

const defaultCustomTableColumns = () => [
  { id: newColumnId(), header: 'Time', field: 'timestamp', fieldMode: 'preset', valueType: 'auto' },
  { id: newColumnId(), header: 'Level', field: 'level', fieldMode: 'preset', valueType: 'auto' },
  { id: newColumnId(), header: 'Service', field: 'service', fieldMode: 'preset', valueType: 'auto' },
  { id: newColumnId(), header: 'Message', field: 'message', fieldMode: 'preset', valueType: 'auto' }
];

const WidgetModal = ({ isOpen, onClose, onSave, widget, darkMode = false }) => {
  const [datasetEditorOpen, setDatasetEditorOpen] = useState(false);
  const [editingDatasetIndex, setEditingDatasetIndex] = useState(null);

  const [formData, setFormData] = useState({
    title: '',
    type: 'line-chart',
    dataSource: 'metrics',
    service: '',
    metric: '',
    logLevel: '',
    keyword: '',
    logql: '',
    timeRange: '24h',
    chartType: 'line',
    theme: 'blue',
    chartColor: 'blue',
    groupBy: 'none',
    datasets: [],
    customEndpoint: '',
    useCustomEndpoint: false,
    customTableColumns: []
  });

  const BACKGROUND_OPTIONS = [
    { value: 'blue', label: 'Blue' },
    { value: 'green', label: 'Green' },
    { value: 'purple', label: 'Purple' },
    { value: 'orange', label: 'Orange' },
    { value: 'red', label: 'Red' },
    { value: 'yellow', label: 'Yellow' },
    { value: 'pink', label: 'Pink' },
    { value: 'teal', label: 'Teal' },
    { value: 'indigo', label: 'Indigo' },
    { value: 'gray', label: 'Gray' }
  ];

  // Load form when the main modal opens or when editing a different widget (by id).
  // Do NOT depend on full `widget` reference — parent re-renders can pass a new object
  // for the same widget and would close the dataset sub-modal and wipe in-progress edits.
  const widgetId = widget?.id ?? null;
  useEffect(() => {
    if (!isOpen) {
      setDatasetEditorOpen(false);
      setEditingDatasetIndex(null);
      return;
    }
    if (widget) {
      const resolvedType = widget.type || 'line-chart';
      setFormData({
        title: widget.title || '',
        type: resolvedType,
        dataSource: resolvedType === 'custom-table' ? 'logs' : widget.dataSource || 'metrics',
        service: widget.service || '',
        metric: widget.metric || '',
        logLevel: widget.logLevel || '',
        keyword: widget.keyword || '',
        logql: widget.logql || '',
        timeRange: widget.timeRange || '24h',
        chartType: widget.chartType || 'line',
        theme: widget.theme || 'blue',
        chartColor: widget.chartColor || 'blue',
        groupBy: widget.groupBy || 'none',
        datasets: Array.isArray(widget.datasets) ? widget.datasets : [],
        customEndpoint: widget.customEndpoint || '',
        useCustomEndpoint: widget.useCustomEndpoint || false,
        customTableColumns:
          Array.isArray(widget.customTableColumns) && widget.customTableColumns.length > 0
            ? widget.customTableColumns.map(migrateCustomTableColumn)
            : widget.type === 'custom-table'
              ? defaultCustomTableColumns()
              : []
      });
    } else {
      setFormData({
        title: '',
        type: 'line-chart',
        dataSource: 'metrics',
        service: '',
        metric: '',
        logLevel: '',
        keyword: '',
        logql: '',
        timeRange: '24h',
        chartType: 'line',
        theme: 'blue',
        chartColor: 'blue',
        groupBy: 'none',
        datasets: [],
        customEndpoint: '',
        useCustomEndpoint: false,
        customTableColumns: []
      });
    }
    setDatasetEditorOpen(false);
    setEditingDatasetIndex(null);
  }, [isOpen, widgetId]);

  const handleSubmit = (e) => {
    e.preventDefault();
    onSave(formData);
  };

  const handleChange = (field, value) => {
    setFormData((prev) => {
      const next = { ...prev, [field]: value };
      if (field === 'type' && value === 'custom-table') {
        next.dataSource = 'logs';
        next.datasets = [];
        if (!Array.isArray(next.customTableColumns) || next.customTableColumns.length === 0) {
          next.customTableColumns = defaultCustomTableColumns();
        }
      }
      return next;
    });
  };

  const updateCustomColumn = (index, patch) => {
    setFormData((prev) => ({
      ...prev,
      customTableColumns: (prev.customTableColumns || []).map((c, i) =>
        i === index ? { ...c, ...patch } : c
      )
    }));
  };

  const addCustomColumn = () => {
    setFormData((prev) => ({
      ...prev,
      customTableColumns: [
        ...(prev.customTableColumns || []),
        { id: newColumnId(), header: 'New column', field: 'message', fieldMode: 'preset', valueType: 'auto' }
      ]
    }));
  };

  const removeCustomColumn = (index) => {
    setFormData((prev) => ({
      ...prev,
      customTableColumns: (prev.customTableColumns || []).filter((_, i) => i !== index)
    }));
  };

  const supportsMultipleDatasets = ['line-chart', 'area-chart', 'bar-chart', 'horizontal-bar-chart', 'pie-chart', 'donut-chart', 'log-table'].includes(formData.type)
    && (formData.dataSource === 'logs' || formData.dataSource === 'metrics');

  const openAddDataset = () => {
    setEditingDatasetIndex(null);
    setDatasetEditorOpen(true);
  };

  const openEditDataset = (index) => {
    setEditingDatasetIndex(index);
    setDatasetEditorOpen(true);
  };

  const handleSaveDataset = (dataset) => {
    setFormData((prev) => {
      const next = [...(prev.datasets || [])];
      if (editingDatasetIndex != null && editingDatasetIndex >= 0) {
        next[editingDatasetIndex] = dataset;
      } else {
        next.push(dataset);
      }
      return { ...prev, datasets: next };
    });
    setDatasetEditorOpen(false);
    setEditingDatasetIndex(null);
  };

  const removeDataset = (index) => {
    setFormData((prev) => ({
      ...prev,
      datasets: (prev.datasets || []).filter((_, i) => i !== index)
    }));
  };

  const moveDataset = (index, dir) => {
    setFormData((prev) => {
      const arr = [...(prev.datasets || [])];
      const j = index + dir;
      if (j < 0 || j >= arr.length) return prev;
      [arr[index], arr[j]] = [arr[j], arr[index]];
      return { ...prev, datasets: arr };
    });
  };

  if (!isOpen) return null;

  const themeClass = darkMode ? 'widget-modal--dark' : 'widget-modal--light';

  return createPortal(
    <>
    <div className={`modal-overlay widget-modal-root ${themeClass}`}>
      <div className="modal">
        <div className="modal-header">
          <h2 className="modal-title">
            {widget ? 'Edit Widget' : 'Add New Widget'}
          </h2>
          <button
            onClick={onClose}
            className="modal-close"
          >
            <X size={24} />
          </button>
        </div>

        <form onSubmit={handleSubmit} className="modal-body">
          {/* Widget Title */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Widget Title
            </label>
            <input
              type="text"
              value={formData.title}
              onChange={(e) => handleChange('title', e.target.value)}
              className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
              required
            />
          </div>

          {/* Widget Type */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Widget Type
            </label>
            <select
              value={formData.type}
              onChange={(e) => handleChange('type', e.target.value)}
              className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
            >
              <option value="line-chart">Line Chart</option>
              <option value="area-chart">Area Chart</option>
              <option value="bar-chart">Bar Chart</option>
              <option value="horizontal-bar-chart">Horizontal Bar Chart</option>
              <option value="pie-chart">Pie Chart</option>
              <option value="donut-chart">Donut Chart</option>
              <option value="single-metric">Single Metric</option>
              <option value="log-table">Log Table</option>
              <option value="custom-table">Custom Table</option>
            </select>
          </div>

          {/* Custom table: column definitions (rows use dashboard sidebar time range) */}
          {formData.type === 'custom-table' && (
            <div className="custom-table-editor border border-gray-200 rounded-lg p-3 bg-gray-50">
              <label className="block text-sm font-medium text-gray-800 mb-1">Table columns</label>
              <p className="text-xs text-gray-600 mb-3">
                Set each column header, pick a built-in log field or a <strong>custom property</strong> (key or{' '}
                <code className="text-[11px] bg-gray-200 px-0.5 rounded">nested.path</code>), and choose how values
                are formatted (text, numbers, dates, keywords). Data uses the sidebar <strong>Time Range</strong>.
              </p>
              <div className="space-y-3">
                {(formData.customTableColumns || []).map((col, idx) => {
                  const fieldMode = col.fieldMode === 'custom' ? 'custom' : 'preset';
                  const dataSelectValue = fieldMode === 'custom' ? CUSTOM_FIELD_SELECT_CUSTOM : col.field;
                  return (
                    <div
                      key={col.id || idx}
                      className="rounded-md border border-gray-200 bg-white p-2.5 space-y-2 shadow-sm"
                    >
                      <div className="flex flex-wrap items-center gap-2">
                        <input
                          type="text"
                          value={col.header}
                          onChange={(e) => updateCustomColumn(idx, { header: e.target.value })}
                          placeholder="Column header (shown in table)"
                          className="flex-1 min-w-[140px] px-2 py-1.5 border border-gray-300 rounded-md text-sm"
                        />
                        <button
                          type="button"
                          onClick={() => removeCustomColumn(idx)}
                          className="p-1.5 rounded hover:bg-gray-100 text-red-600 shrink-0"
                          title="Remove column"
                          aria-label="Remove column"
                        >
                          <Trash2 size={16} />
                        </button>
                      </div>
                      <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
                        <div>
                          <label className="block text-xs font-medium text-gray-600 mb-0.5">Column data</label>
                          <select
                            value={dataSelectValue}
                            onChange={(e) => {
                              const v = e.target.value;
                              if (v === CUSTOM_FIELD_SELECT_CUSTOM) {
                                updateCustomColumn(idx, {
                                  fieldMode: 'custom',
                                  field: CUSTOM_TABLE_PRESET_FIELD_SET.has(col.field) ? '' : col.field
                                });
                              } else {
                                updateCustomColumn(idx, { fieldMode: 'preset', field: v });
                              }
                            }}
                            className="w-full px-2 py-1.5 border border-gray-300 rounded-md text-sm"
                          >
                            <optgroup label="Log fields">
                              {CUSTOM_TABLE_LOG_FIELDS.map((f) => (
                                <option key={f.value} value={f.value}>
                                  {f.label}
                                </option>
                              ))}
                            </optgroup>
                            <option value={CUSTOM_FIELD_SELECT_CUSTOM}>Custom property…</option>
                          </select>
                        </div>
                        <div>
                          <label className="block text-xs font-medium text-gray-600 mb-0.5">Value type</label>
                          <select
                            value={col.valueType || 'auto'}
                            onChange={(e) => updateCustomColumn(idx, { valueType: e.target.value })}
                            className="w-full px-2 py-1.5 border border-gray-300 rounded-md text-sm"
                          >
                            {CUSTOM_TABLE_VALUE_TYPES.map((t) => (
                              <option key={t.value} value={t.value}>
                                {t.label}
                              </option>
                            ))}
                          </select>
                        </div>
                      </div>
                      {fieldMode === 'custom' && (
                        <div>
                          <label className="block text-xs font-medium text-gray-600 mb-0.5">
                            Property name or path
                          </label>
                          <input
                            type="text"
                            value={col.field}
                            onChange={(e) => updateCustomColumn(idx, { field: e.target.value })}
                            placeholder="e.g. traceId, userId, meta.orderId"
                            className="w-full px-2 py-1.5 border border-gray-300 rounded-md text-sm font-mono"
                          />
                          <p className="text-[11px] text-gray-500 mt-1">
                            Reads from each log object; use dots for nested keys. Unknown paths show —.
                          </p>
                        </div>
                      )}
                    </div>
                  );
                })}
              </div>
              <button
                type="button"
                onClick={addCustomColumn}
                className="mt-3 inline-flex items-center gap-1 px-2 py-1 text-xs font-medium text-white bg-blue-600 rounded-md hover:bg-blue-700"
              >
                <Plus size={14} /> Add column
              </button>
            </div>
          )}

          {/* Data Source */}
          {formData.type !== 'custom-table' ? (
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Data Source
              </label>
              <select
                value={formData.dataSource}
                onChange={(e) => handleChange('dataSource', e.target.value)}
                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
              >
                <option value="metrics">Metrics</option>
                <option value="logs">Logs</option>
              </select>
            </div>
          ) : (
            <div className="rounded-md border border-dashed border-gray-300 px-3 py-2 text-sm text-gray-600">
              Data source: <strong>Logs</strong> (custom tables always use stored logs).
            </div>
          )}

          {/* Service Selection (hidden when logs + per-series datasets take over) */}
          {!(formData.dataSource === 'logs' && supportsMultipleDatasets && (formData.datasets || []).length > 0) && (
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Service
              </label>
              <select
                value={formData.service}
                onChange={(e) => handleChange('service', e.target.value)}
                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
              >
                <option value="">All Services</option>
                <option value="api-gateway">API Gateway</option>
                <option value="user-service">User Service</option>
                <option value="order-service">Order Service</option>
                <option value="payment-service">Payment Service</option>
                <option value="notification-service">Notification Service</option>
                <option value="auth-service">Auth Service</option>
              </select>
            </div>
          )}

          {/* Metric Selection (for metrics data source) */}
          {formData.dataSource === 'metrics' && (
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Metric
              </label>
              <select
                value={formData.metric}
                onChange={(e) => handleChange('metric', e.target.value)}
                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
              >
                <option value="">All Metrics</option>
                <option value="cpu">CPU Usage</option>
                <option value="memory">Memory Usage</option>
                <option value="disk">Disk Usage</option>
                <option value="network">Network I/O</option>
              </select>
            </div>
          )}

          {/* Log Level (for logs data source) */}
          {formData.dataSource === 'logs' && (
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Log Level
              </label>
              <select
                value={formData.logLevel}
                onChange={(e) => handleChange('logLevel', e.target.value)}
                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
              >
                <option value="">All Levels</option>
                <option value="ERROR">Error</option>
                <option value="WARN">Warning</option>
                <option value="INFO">Info</option>
                <option value="DEBUG">Debug</option>
                <option value="TRACE">Trace</option>
              </select>
            </div>
          )}

          {/* Keyword and LogQL filters (logs data source) */}
          {formData.dataSource === 'logs' && (!supportsMultipleDatasets || (formData.datasets || []).length === 0) && (
            <>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Keyword (message contains)
                </label>
                <input
                  type="text"
                  value={formData.keyword}
                  onChange={(e) => handleChange('keyword', e.target.value)}
                  placeholder="e.g. timeout, error, 500"
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  LogQL query (advanced)
                </label>
                <textarea
                  value={formData.logql}
                  onChange={(e) => handleChange('logql', e.target.value)}
                  placeholder={'level IN ("ERROR","WARN") AND message CONTAINS "timeout"'}
                  rows={6}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                />
                <p className="mt-1 text-xs text-gray-500">
                  Optional. Examples: <code>message CONTAINS "foo"</code>, <code>level IN ("ERROR")</code>, <code>service CONTAINS "api"</code>.
                </p>
              </div>
            </>
          )}

          {/* Multiple data series (sub-windows per dataset) */}
          {supportsMultipleDatasets && (
            <div className="border border-gray-200 rounded-lg p-3 bg-gray-50">
              <div className="flex items-center justify-between mb-2">
                <label className="block text-sm font-medium text-gray-800">
                  Data series (multiple datasets)
                </label>
                <button
                  type="button"
                  onClick={openAddDataset}
                  className="inline-flex items-center gap-1 px-2 py-1 text-xs font-medium text-white bg-blue-600 rounded-md hover:bg-blue-700"
                >
                  <Plus size={14} /> Add series
                </button>
              </div>
              <p className="text-xs text-gray-600 mb-2">
                When you add one or more series, each opens in its own editor. Charts show one line/bar per series (distinct colors).
                {(formData.datasets || []).length > 0 && formData.dataSource === 'logs' && (
                  <span className="block mt-1">Widget-level log filters above are ignored while series are defined; use each series&apos; filters instead.</span>
                )}
              </p>
              {(formData.datasets || []).length === 0 ? (
                <p className="text-xs text-gray-500 italic">No extra series — using the single widget filters below.</p>
              ) : (
                <ul className="space-y-2">
                  {(formData.datasets || []).map((ds, idx) => (
                    <li
                      key={ds.id || idx}
                      className="flex items-center gap-2 flex-wrap bg-white border border-gray-200 rounded-md px-2 py-2"
                    >
                      <span className="flex-1 min-w-[120px] text-sm font-medium text-gray-800">{ds.label || `Series ${idx + 1}`}</span>
                      <span className="text-xs text-gray-500">
                        {formData.dataSource === 'metrics' ? (ds.metric || 'cpu') : (ds.service || 'all svc')}
                      </span>
                      <div className="flex items-center gap-1">
                        <button type="button" className="p-1 rounded hover:bg-gray-100" title="Move up" onClick={() => moveDataset(idx, -1)}>
                          <ChevronUp size={16} />
                        </button>
                        <button type="button" className="p-1 rounded hover:bg-gray-100" title="Move down" onClick={() => moveDataset(idx, 1)}>
                          <ChevronDown size={16} />
                        </button>
                        <button type="button" className="p-1 rounded hover:bg-gray-100 text-blue-600" title="Edit" onClick={() => openEditDataset(idx)}>
                          <Pencil size={16} />
                        </button>
                        <button type="button" className="p-1 rounded hover:bg-gray-100 text-red-600" title="Remove" onClick={() => removeDataset(idx)}>
                          <Trash2 size={16} />
                        </button>
                      </div>
                    </li>
                  ))}
                </ul>
              )}
            </div>
          )}

          {/* Time Range (chart widgets; custom table uses dashboard sidebar range) */}
          {formData.type !== 'custom-table' && (
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Time Range
              </label>
              <select
                value={formData.timeRange}
                onChange={(e) => handleChange('timeRange', e.target.value)}
                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
              >
                <option value="1h">Last Hour</option>
                <option value="6h">Last 6 Hours</option>
                <option value="24h">Last 24 Hours</option>
                <option value="7d">Last 7 Days</option>
                <option value="30d">Last 30 Days</option>
              </select>
            </div>
          )}

          {/* Widget Background Color - dropdown */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Widget Background
            </label>
            <select
              value={formData.theme}
              onChange={(e) => handleChange('theme', e.target.value)}
              className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
            >
              {BACKGROUND_OPTIONS.map((opt) => (
                <option key={opt.value} value={opt.value}>{opt.label}</option>
              ))}
            </select>
          </div>

          {/* Group series by (for charts with logs data - multiple lines/bars/areas) */}
          {['line-chart', 'area-chart', 'bar-chart', 'horizontal-bar-chart'].includes(formData.type) && formData.dataSource === 'logs' && (formData.datasets || []).length === 0 && (
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Split data by (multiple series)
              </label>
              <select
                value={formData.groupBy}
                onChange={(e) => handleChange('groupBy', e.target.value)}
                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
              >
                <option value="none">Single series (total count)</option>
                <option value="level">Log level (ERROR, WARN, INFO, …)</option>
                <option value="service">Service</option>
              </select>
              <p className="mt-1 text-xs text-gray-500">
                Each series gets a different color on the chart.
              </p>
            </div>
          )}

          {/* Chart Line/Bar Color (single series, or base color for pie/donut) */}
          {['line-chart', 'area-chart', 'bar-chart', 'horizontal-bar-chart', 'pie-chart', 'donut-chart'].includes(formData.type) && (
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Chart color {formData.groupBy !== 'none' ? '(used when single series)' : ''}
              </label>
              <select
                value={formData.chartColor}
                onChange={(e) => handleChange('chartColor', e.target.value)}
                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
              >
                {BACKGROUND_OPTIONS.map((opt) => (
                  <option key={opt.value} value={opt.value}>{opt.label}</option>
                ))}
              </select>
            </div>
          )}

          {/* Custom Endpoint Configuration */}
          <div className="border-t pt-4">
            <div className="flex items-center mb-3">
              <input
                type="checkbox"
                id="useCustomEndpoint"
                checked={formData.useCustomEndpoint}
                onChange={(e) => handleChange('useCustomEndpoint', e.target.checked)}
                className="mr-2"
              />
              <label htmlFor="useCustomEndpoint" className="text-sm font-medium text-gray-700">
                Use Custom Data Endpoint
              </label>
            </div>
            
            {formData.useCustomEndpoint && (
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Custom API Endpoint
                </label>
                <input
                  type="text"
                  value={formData.customEndpoint}
                  onChange={(e) => handleChange('customEndpoint', e.target.value)}
                  placeholder="https://api.example.com/metrics"
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                />
                <p className="mt-1 text-xs text-gray-500">
                  Enter the full URL of your custom monitoring endpoint
                </p>
              </div>
            )}
          </div>

          {/* Action Buttons */}
          <div className="flex justify-end space-x-3 pt-4 border-t">
            <button
              type="button"
              onClick={onClose}
              className="px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-md hover:bg-gray-50"
            >
              Cancel
            </button>
            <button
              type="submit"
              className="px-4 py-2 text-sm font-medium text-white bg-blue-600 border border-transparent rounded-md hover:bg-blue-700"
            >
              {widget ? 'Update Widget' : 'Add Widget'}
            </button>
          </div>
        </form>
      </div>

      <DatasetEditorModal
        isOpen={datasetEditorOpen}
        onClose={() => {
          setDatasetEditorOpen(false);
          setEditingDatasetIndex(null);
        }}
        onSave={handleSaveDataset}
        dataset={editingDatasetIndex != null ? formData.datasets[editingDatasetIndex] : null}
        dataSource={formData.dataSource}
        darkMode={darkMode}
      />
    </div>
    </>,
    document.body
  );
};

export default WidgetModal;
